import React, { useEffect, useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, getErr } from "../lib/api";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh","Chandigarh","Puducherry","Andaman and Nicobar Islands","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep",
];
const STATUSES = ["New", "Contacted", "In Progress", "Pending", "Completed", "Rejected"];

/**
 * Edit existing lead or customer.
 * Props: open, onClose, kind ('lead'|'customer'), record, onSaved(updated)
 */
export default function EditRecordDialog({ open, onClose, kind, record, onSaved }) {
  const [form, setForm] = useState({});
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !record) return;
    setForm({
      full_name: record.full_name || "",
      mobile: record.mobile || "",
      whatsapp: record.whatsapp || "",
      email: record.email || "",
      pan: record.pan || "",
      aadhaar: record.aadhaar || "",
      state: record.state || "",
      district: record.district || "",
      city: record.city || "",
      address: record.address || "",
      pincode: record.pincode || "",
      ...(kind === "lead" ? {
        service_id: record.service_id || "",
        status: record.status || "New",
        remarks: record.remarks || "",
      } : {}),
    });
    api.get("/admin/services").then((r) => setServices(r.data.services || [])).catch(() => {
      api.get("/services").then((r) => setServices(r.data.services || [])).catch(() => {});
    });
  }, [open, record, kind]);

  if (!open || !record) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSave = async (e) => {
    e?.preventDefault();
    if (!form.full_name?.trim()) return toast.error("Full name is required");
    if (!form.mobile || form.mobile.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit mobile");
    setSaving(true);
    try {
      // Send only changed fields to reduce blast radius
      const changes = {};
      for (const k of Object.keys(form)) {
        if ((form[k] ?? "") !== (record[k] ?? "")) changes[k] = form[k];
      }
      if (Object.keys(changes).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      const url = kind === "customer"
        ? `/admin/customers/${record.id}`
        : `/admin/leads/${record.id}`;
      await api.patch(url, changes);
      toast.success(`${kind === "customer" ? "Customer" : "Lead"} updated`);
      onSaved && onSaved(changes);
    } catch (e) {
      toast.error(getErr(e, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-3" data-testid={`edit-${kind}-dialog`}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E2DC] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <span className="label-eyebrow text-[10px]">CRM</span>
            <h2 className="font-display text-xl font-medium text-[#121C17] mt-0.5">Edit {kind} · <span className="font-mono text-base text-[#5C6661]">{record.ref}</span></h2>
            <p className="text-xs text-[#5C6661] mt-0.5">Only the fields you change will be sent to the server.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-[#F3F1EC]" data-testid={`edit-${kind}-close`} aria-label="Close"><X size={18} /></button>
        </div>

        <form onSubmit={onSave} className="px-6 py-5 space-y-5" data-testid={`edit-${kind}-form`}>
          <Section title="Identity">
            <Grid>
              <Field label="Full name *" v={form.full_name} on={(v) => set("full_name", v)} test={`edit-${kind}-full_name`} />
              <Field label="Mobile *" v={form.mobile} on={(v) => set("mobile", v.replace(/\D/g, "").slice(0, 10))} test={`edit-${kind}-mobile`} />
              <Field label="WhatsApp" v={form.whatsapp} on={(v) => set("whatsapp", v.replace(/\D/g, "").slice(0, 10))} test={`edit-${kind}-whatsapp`} />
              <Field label="Email" v={form.email} on={(v) => set("email", v)} test={`edit-${kind}-email`} type="email" />
              <Field label="PAN" v={form.pan} on={(v) => set("pan", v.toUpperCase().slice(0, 10))} test={`edit-${kind}-pan`} mono />
              <Field label="Aadhaar" v={form.aadhaar} on={(v) => set("aadhaar", v.replace(/\D/g, "").slice(0, 12))} test={`edit-${kind}-aadhaar`} mono />
            </Grid>
          </Section>

          {kind === "lead" && (
            <Section title="Service & Status">
              <Grid>
                <SelectField label="Service" v={form.service_id} on={(v) => set("service_id", v)} test="edit-lead-service">
                  {services.map((s) => (
                    <option key={s.id} value={s.id} disabled={s.is_active === false && s.id !== form.service_id}>
                      {s.name} · ₹{(s.price || 0).toLocaleString("en-IN")}{s.is_active === false ? " · (inactive)" : ""}
                    </option>
                  ))}
                </SelectField>
                <SelectField label="Status" v={form.status} on={(v) => set("status", v)} test="edit-lead-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </SelectField>
              </Grid>
            </Section>
          )}

          <Section title="Address">
            <Grid>
              <SelectField label="State" v={form.state} on={(v) => set("state", v)} test={`edit-${kind}-state`}>
                <option value="">— Select —</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </SelectField>
              <Field label="District" v={form.district} on={(v) => set("district", v)} test={`edit-${kind}-district`} />
              <Field label="City" v={form.city} on={(v) => set("city", v)} test={`edit-${kind}-city`} />
              <Field label="PIN code" v={form.pincode} on={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} test={`edit-${kind}-pincode`} />
            </Grid>
            <div className="mt-3">
              <Field label="Address" v={form.address} on={(v) => set("address", v)} test={`edit-${kind}-address`} />
            </div>
          </Section>

          {kind === "lead" && (
            <Section title="Notes">
              <textarea
                value={form.remarks || ""}
                onChange={(e) => set("remarks", e.target.value)}
                data-testid="edit-lead-remarks"
                rows={2}
                className="w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]"
              />
            </Section>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[#E5E2DC]">
            <button type="button" onClick={onClose} data-testid={`edit-${kind}-cancel`} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]">Cancel</button>
            <button type="submit" disabled={saving} data-testid={`edit-${kind}-submit`} className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="label-eyebrow text-[10px] mb-3">{title}</h3>
    {children}
  </div>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;

const Field = ({ label, v, on, test, type = "text", mono }) => (
  <label className="block">
    <span className="text-xs text-[#5C6661]">{label}</span>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={test}
      className={`mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B] ${mono ? "font-mono" : ""}`} />
  </label>
);

const SelectField = ({ label, v, on, test, children }) => (
  <label className="block">
    <span className="text-xs text-[#5C6661]">{label}</span>
    <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={test}
      className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]">
      {children}
    </select>
  </label>
);
